import axios from "axios";
import { offerCreditTopUp } from "../creditTopUp";
import { analyzeImageForRetrace } from "./imageAnalysis";
import { isEmbroideryTrace } from "../../config/appVariant";

const RETRACE_API_URL =
  "https://us-central1-svgtrace-pro.cloudfunctions.net/redrawLowResNanoBanana";

export async function requestRetrace(base64Image, user, { signal, guestMode = false, pendingFileId } = {}) {
  // Pending-file editing is not anonymous billing in EmbroideryTrace.
  const authenticated = isEmbroideryTrace || !guestMode;
  if (authenticated && !user) throw new Error("Please sign in to use Retrace.");
  const headers = { "Content-Type": "application/json" };
  if (authenticated) headers.Authorization = `Bearer ${await user.getIdToken()}`;
  const body = { base64Image: base64Image.split(",")[1] || base64Image };
  let url = RETRACE_API_URL;
  if (isEmbroideryTrace) {
    url = "https://us-central1-svgtrace-pro.cloudfunctions.net/embroideryRetrace";
    body.requestId = crypto.randomUUID();
  } else if (guestMode) {
    url = "https://us-central1-svgtrace-pro.cloudfunctions.net/retraceImageGuest";
    body.pendingFileId = pendingFileId;
  }
  // No automatic HTTP retry: a timeout must not repeat paid provider work.
  try {
    return await axios.post(url, body, { headers, timeout: isEmbroideryTrace ? 540000 : 180000, signal });
  } catch (error) {
    offerCreditTopUp(error, 'Retrace');
    throw error;
  }
}

/**
 * Determine whether an image should be AI-retraced before vector conversion.
 * Combines resolution-based detection (from processAndAnalyzeImage) with
 * Gemini analysis for gradients/photos/complex shading.
 */
export async function checkRetraceNeeded(base64Image, processedResult = null) {
  let needsRetrace = processedResult?.wasUpscaled || false;
  let reason = processedResult?.upscaleReason || "";

  try {
    const aiAnalysis = await analyzeImageForRetrace(base64Image);
    if (aiAnalysis.needsRetrace) {
      needsRetrace = true;
      reason = aiAnalysis.hasGradients ? "gradients" : "ai-analysis";
    }
  } catch (error) {
    console.warn(
      "Retrace analysis failed, using resolution-based decision only:",
      error
    );
  }

  return { needsRetrace, reason };
}

/**
 * Call the Nano Banana retrace endpoint for authenticated users.
 * Returns a data-URL PNG of the retraced image.
 */
export async function retraceImageWithAI(base64Image, user, signal = null) {
  if (!user) {
    throw new Error("User must be signed in for AI retrace");
  }

  const resp = await requestRetrace(base64Image, user, { signal });

  return `data:image/png;base64,${resp.data.base64}`;
}
